import { alpha } from '@material-ui/core/styles'
import theme from 'material/theme'

// Valid tokens that will be highlighted green
export const VALID_TOKENS = ['Preferred_Name']

// Regex to match token patterns like {{Token_Name}}
const TOKEN_REGEX = /\{\{([^}]+)\}\}/g

// CSS classes for token highlighting (used for test assertions)
export const TOKEN_VALID_CLASS = 'token-valid'
export const TOKEN_INVALID_CLASS = 'token-invalid'

// Color constants for token validation states from the theme palette
export const TOKEN_COLORS = {
  valid: theme.palette.success.main,
  validBackground: alpha(theme.palette.success.main, 0.08),
  validBorder: theme.palette.success.light,
  invalid: theme.palette.error.main,
  invalidBackground: alpha(theme.palette.error.main, 0.08),
  invalidBorder: theme.palette.error.light,
}

// Inline styles for token highlighting (more reliable in CKEditor)
const TOKEN_BASE_STYLE =
  'display: inline-block; padding: 2px 6px; border-radius: 12px; font-family: monospace; font-size: 0.9em; white-space: nowrap;'
const TOKEN_VALID_STYLE = `${TOKEN_BASE_STYLE} background-color: ${TOKEN_COLORS.validBackground}; color: ${TOKEN_COLORS.valid}; border: 1px solid ${TOKEN_COLORS.validBorder};`
const TOKEN_INVALID_STYLE = `${TOKEN_BASE_STYLE} background-color: ${TOKEN_COLORS.invalidBackground}; color: ${TOKEN_COLORS.invalid}; border: 1px solid ${TOKEN_COLORS.invalidBorder};`

/**
 * Check if a token name is valid
 */
export const isValidToken = (tokenName) => VALID_TOKENS.includes(tokenName)

/**
 * Check if text contains any invalid tokens
 * @param {string} text - Text to check for invalid tokens
 * @returns {boolean} True if text contains invalid tokens, false otherwise
 */
export const hasInvalidTokens = (text) => {
  const tokens = extractTokens(text)
  return tokens.some((token) => !isValidToken(token))
}

/**
 * Extract all token names from text
 * @param {string} text - Text to extract tokens from
 * @returns {string[]} Array of token names (without the {{ }} wrapper)
 */
export const extractTokens = (text) => {
  if (!text) return []

  const tokens = []
  let match

  // Create a new RegExp to avoid issues with lastIndex state
  const regex = /\{\{([^}]+)\}\}/g
  while ((match = regex.exec(text)) !== null) {
    tokens.push(match[1])
  }

  return tokens
}

/**
 * Visually hidden style for screen reader only text
 * This text is not visible but will be read by screen readers
 */
const SR_ONLY_STYLE =
  'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;'

/**
 * Add highlighting spans to content for display in CKEditor
 * Wraps {{Token}} patterns in styled spans with inline styles
 * Tokens remain editable - user can click into them to fix typos
 * Skips tokens that are already highlighted
 * Includes visually-hidden text for screen reader accessibility
 */
export const addTokenHighlighting = (html) => {
  if (!html) return html

  // First, strip any existing highlighting to start fresh
  const stripped = stripTokenHighlighting(html)

  // Then apply highlighting to all tokens
  return stripped.replace(TOKEN_REGEX, (match, tokenName) => {
    const isValid = isValidToken(tokenName)
    const className = isValid ? TOKEN_VALID_CLASS : TOKEN_INVALID_CLASS
    const style = isValid ? TOKEN_VALID_STYLE : TOKEN_INVALID_STYLE
    const srText = isValid ? 'valid token' : 'invalid token'
    // Add visually-hidden text before the token for screen readers
    const srOnlySpan = `<span style="${SR_ONLY_STYLE}" data-sr-only="true">${srText}: </span>`
    return `<span class="${className}" style="${style}" data-token-highlight="true">${srOnlySpan}${match}</span>`
  })
}

/**
 * Remove highlighting spans from content for saving
 * Strips the wrapper spans and sr-only spans but keeps the token text
 */
export const stripTokenHighlighting = (html) => {
  if (!html) return html

  // First remove sr-only spans entirely (they contain no user content)
  let result = html.replace(/<span[^>]*data-sr-only="true"[^>]*>[^<]*<\/span>/g, '')

  // Then remove highlight wrapper spans but keep inner content
  result = result.replace(
    /<span[^>]*data-token-highlight="true"[^>]*>(.*?)<\/span>/g,
    '$1'
  )

  return result
}

/**
 * Configure CKEditor instance for token highlighting
 * Call this after CKEditor instance is ready
 */
export const setupTokenHighlighting = (editorInstance) => {
  if (!editorInstance) return

  // Add filter to strip highlighting on getData (save)
  // This ensures token highlighting spans are not persisted to the database
  if (editorInstance.dataProcessor && editorInstance.dataProcessor.htmlFilter) {
    editorInstance.dataProcessor.htmlFilter.addRules({
      elements: {
        span: (element) => {
          // Remove sr-only spans entirely (they're for screen readers only, not user content)
          if (element.attributes && element.attributes['data-sr-only'] === 'true') {
            return false // Returning false removes the element entirely
          }
          if (element.attributes && element.attributes['data-token-highlight'] === 'true') {
            // Replace the span with just its text content
            delete element.name
            return element
          }
          return element
        },
      },
    })
  }

  // Debounce timer for applying highlighting
  let highlightTimer = null

  // Listen for content changes and reapply highlighting
  editorInstance.on('change', () => {
    // Debounce to avoid excessive processing while typing
    if (highlightTimer) {
      clearTimeout(highlightTimer)
    }
    highlightTimer = setTimeout(() => {
      applyHighlightingToEditor(editorInstance)
    }, 300)
  })
}

/**
 * Check if HTML contains unhighlighted tokens
 * Returns true if there are {{...}} patterns not wrapped in highlight spans
 */
const hasUnhighlightedTokens = (html) => {
  if (!html) return false

  // Remove all highlighted spans to see what's left
  const withoutHighlighted = html.replace(
    /<span[^>]*data-token-highlight="true"[^>]*>.*?<\/span>/g,
    ''
  )

  // Check if there are still unhighlighted tokens
  return /\{\{[^}]+\}\}/.test(withoutHighlighted)
}

/**
 * Check if any highlighted span contains content that is no longer a valid token format
 * Returns true if a span contains malformed token (e.g., {{Name} with missing brace)
 */
const hasMalformedHighlightedTokens = (html) => {
  if (!html) return false

  // Find all highlighted token spans
  // The structure is: <span data-token-highlight><span data-sr-only>text</span>{{Token}}</span>
  // We need to extract the token text after the sr-only span
  const highlightedSpanRegex =
    /<span[^>]*data-token-highlight="true"[^>]*>(?:<span[^>]*data-sr-only="true"[^>]*>[^<]*<\/span>)?([^<]*)<\/span>/g

  let match
  while ((match = highlightedSpanRegex.exec(html)) !== null) {
    const content = match[1]

    // Check if content is still a valid token format ({{...}})
    // Must start with {{ and end with }} with content in between
    // If it doesn't match the token pattern, the span should be removed
    if (!/^\{\{.+\}\}$/.test(content)) {
      return true
    }
  }

  return false
}

/**
 * Check if any highlighted token has incorrect validity styling
 * Returns true if a token's validity changed (e.g., typo was fixed)
 */
const hasIncorrectlyHighlightedTokens = (html) => {
  if (!html) return false

  // Find all highlighted token spans (with data-token-highlight attribute)
  // Use a flexible regex that doesn't depend on attribute order
  const highlightedSpanRegex =
    /<span([^>]*data-token-highlight="true"[^>]*)>(.*?)<\/span>/g

  let match
  while ((match = highlightedSpanRegex.exec(html)) !== null) {
    const attributes = match[1]
    const content = match[2]

    // Extract the token name from the content (handles {{Token_Name}})
    const tokenMatch = content.match(/\{\{([^}]+)\}\}/)
    if (!tokenMatch) continue

    const tokenName = tokenMatch[1]

    // Check if span has valid or invalid class
    const hasValidClass = attributes.includes(`class="${TOKEN_VALID_CLASS}"`) ||
      attributes.includes(`class='${TOKEN_VALID_CLASS}'`) ||
      attributes.includes(TOKEN_VALID_CLASS)
    const hasInvalidClass = attributes.includes(`class="${TOKEN_INVALID_CLASS}"`) ||
      attributes.includes(`class='${TOKEN_INVALID_CLASS}'`) ||
      attributes.includes(TOKEN_INVALID_CLASS)

    const shouldBeValid = isValidToken(tokenName)

    // Check for mismatch: valid token with invalid styling or vice versa
    if (shouldBeValid && hasInvalidClass) {
      return true
    }
    if (!shouldBeValid && hasValidClass) {
      return true
    }
  }

  return false
}

/**
 * Apply highlighting to current editor content
 * Use this after loading content or inserting tokens
 */
export const applyHighlightingToEditor = (editorInstance) => {
  if (!editorInstance) return

  // Get raw HTML from the editable element to check for unhighlighted tokens
  // This avoids getData() which runs through filters
  const editable = editorInstance.editable()
  if (!editable) return

  const rawHtml = editable.getHtml()

  const hasMalformed = hasMalformedHighlightedTokens(rawHtml)

  // Check for unhighlighted tokens, tokens with incorrect validity styling, or malformed tokens
  if (!hasUnhighlightedTokens(rawHtml) && !hasIncorrectlyHighlightedTokens(rawHtml) && !hasMalformed) return

  // Get the processed data for highlighting
  const currentData = editorInstance.getData()
  const highlighted = addTokenHighlighting(currentData)

  // Only proceed if highlighting actually changed the content
  // BUT always proceed if we have malformed tokens (they need their spans removed)
  if (highlighted === currentData && !hasMalformed) return

  // Try to save cursor position before setData
  let savedOffset = null
  try {
    const selection = editorInstance.getSelection()
    if (selection) {
      const ranges = selection.getRanges()
      if (ranges && ranges.length > 0) {
        const range = ranges[0]
        // Get the text offset from the start of the editable content
        const editable = editorInstance.editable()
        if (editable && range.startContainer) {
          // Calculate character offset by walking the DOM
          savedOffset = getTextOffset(editable.$, range.startContainer.$, range.startOffset)
        }
      }
    }
  } catch {
    // Ignore errors saving cursor position
  }

  // Update content
  editorInstance.setData(highlighted, { noSnapshot: true })

  // After setData, restore cursor position or move to end
  setTimeout(() => {
    try {
      const editable = editorInstance.editable()
      if (!editable) return

      // Get all token highlight spans
      const tokenSpans = editable.$.querySelectorAll('[data-token-highlight="true"]')
      const lastTokenSpan = tokenSpans.length > 0 ? tokenSpans[tokenSpans.length - 1] : null

      // Check if the last token span is at the end of content
      if (lastTokenSpan) {
        const lastNode = editable.$.lastChild
        // If last node is a paragraph/block element, check its last child
        const lastContentNode = lastNode?.lastChild || lastNode

        // If the token span is at the very end, place cursor after it
        if (lastContentNode === lastTokenSpan ||
            (lastContentNode?.nodeType === 3 && lastContentNode.textContent === '' && lastContentNode.previousSibling === lastTokenSpan)) {
          // Insert a zero-width space after the token to ensure cursor can be placed outside
          const textNode = document.createTextNode('\u200B')
          lastTokenSpan.parentNode.insertBefore(textNode, lastTokenSpan.nextSibling)
        }
      }

      // Try to restore cursor position
      if (savedOffset !== null) {
        const result = setTextOffset(editable.$, savedOffset)
        if (result) {
          const range = editorInstance.createRange()
          range.setStart(new CKEDITOR.dom.node(result.node), result.offset)
          range.collapse(true)
          editorInstance.getSelection().selectRanges([range])
          editorInstance.focus()
          return
        }
      }

      // Fallback: move to end
      const range = editorInstance.createRange()
      range.moveToElementEditEnd(editable)
      editorInstance.getSelection().selectRanges([range])
      editorInstance.focus()
    } catch {
      // Ignore cursor positioning errors
    }
  }, 0)
}

/**
 * Check if a node is inside an sr-only span (screen reader only, hidden text)
 */
const isInsideSrOnlySpan = (node) => {
  let current = node
  while (current && current.nodeType !== 9) { // 9 = Document
    if (current.nodeType === 1 && current.getAttribute && current.getAttribute('data-sr-only') === 'true') {
      return true
    }
    current = current.parentNode
  }
  return false
}

/**
 * Get the character offset from the start of a container to a specific node/offset
 * Skips text inside sr-only spans (hidden accessibility text)
 */
const getTextOffset = (container, targetNode, targetOffset) => {
  let offset = 0
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false)

  let node
  while ((node = walker.nextNode())) {
    // Skip text nodes inside sr-only spans
    if (isInsideSrOnlySpan(node)) {
      continue
    }
    if (node === targetNode) {
      return offset + targetOffset
    }
    offset += node.textContent.length
  }

  // If target is not a text node, try to find the position
  if (targetNode.nodeType !== 3) {
    // Count text length up to the target offset child
    let count = 0
    for (let i = 0; i < targetOffset && i < targetNode.childNodes.length; i++) {
      const child = targetNode.childNodes[i]
      // Skip sr-only spans when counting
      if (child.nodeType === 1 && child.getAttribute && child.getAttribute('data-sr-only') === 'true') {
        continue
      }
      count += child.textContent?.length || 0
    }
    return offset + count
  }

  return offset
}

/**
 * Set cursor at a specific character offset within a container
 * Skips text inside sr-only spans (hidden accessibility text)
 */
const setTextOffset = (container, targetOffset) => {
  let offset = 0
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false)

  let node
  while ((node = walker.nextNode())) {
    // Skip text nodes inside sr-only spans
    if (isInsideSrOnlySpan(node)) {
      continue
    }
    const nodeLength = node.textContent.length
    if (offset + nodeLength >= targetOffset) {
      return { node, offset: targetOffset - offset }
    }
    offset += nodeLength
  }

  return null
}
